import { useEffect, useRef } from 'react';
import { sseService } from '../services/sse';
import { isTrustedShellOrigin } from '../constants/origins';

interface SSEHookOptions {
  onAccountActivated?: (data: any) => void;
  onAccountFailed?: (data: any) => void;
  onWorkflowActivated?: (data: any) => void;
  onWorkflowFailed?: (data: any) => void;
  onWorkflowCompleted?: (data: any) => void;
  onTikTokQRReady?: (data: { account_id: string; qr_base64: string; expires_at: number }) => void;
  enabled?: boolean;
}

export const useSSE = (options: SSEHookOptions) => {
  const {
    onAccountActivated,
    onAccountFailed,
    onWorkflowActivated,
    onWorkflowFailed,
    onWorkflowCompleted,
    onTikTokQRReady,
    enabled = true,
  } = options;

  // Keep handlers up-to-date without causing effect re-runs.
  const handlersRef = useRef({
    onAccountActivated,
    onAccountFailed,
    onWorkflowActivated,
    onWorkflowFailed,
    onWorkflowCompleted,
    onTikTokQRReady,
  });

  useEffect(() => {
    handlersRef.current = {
      onAccountActivated,
      onAccountFailed,
      onWorkflowActivated,
      onWorkflowFailed,
      onWorkflowCompleted,
      onTikTokQRReady,
    };
  }, [onAccountActivated, onAccountFailed, onWorkflowActivated, onWorkflowFailed, onWorkflowCompleted, onTikTokQRReady]);

  useEffect(() => {
    if (!enabled) return;

    // ── Iframe mode: receive SSE events relayed from biztada-app shell ────────
    // When this app runs inside the shell's iframe the shell owns the EventSource
    // and forwards events via postMessage.  Direct SSE connection is skipped to
    // avoid N duplicate connections to the same backend.
    if (window !== window.parent) {
      const processEvent = (sseEvent: { type: string; payload: any }) => {
        switch (sseEvent.type) {
          case 'account_activated':
            console.log('🔔 [useSSE] account_activated (shell relay)', sseEvent.payload);
            handlersRef.current.onAccountActivated?.(sseEvent.payload);
            break;
          case 'account_failed':
            handlersRef.current.onAccountFailed?.(sseEvent.payload);
            break;
          case 'workflow_activated':
            handlersRef.current.onWorkflowActivated?.(sseEvent.payload);
            break;
          case 'workflow_failed':
            handlersRef.current.onWorkflowFailed?.(sseEvent.payload);
            break;
          case 'workflow_completed':
            handlersRef.current.onWorkflowCompleted?.(sseEvent.payload);
            break;
          case 'tiktok_qr_ready':
            handlersRef.current.onTikTokQRReady?.(sseEvent.payload);
            break;
        }
      };

      const handleShellMessage = (event: MessageEvent) => {
        if (!isTrustedShellOrigin(event.origin)) return;
        if (event.data?.source !== 'biztada-shell') return;

        if (event.data.type === 'SSE_EVENT') {
          processEvent(event.data.event);
        } else if (event.data.type === 'SSE_CATCHUP') {
          (event.data.events as { type: string; payload: any }[]).forEach(processEvent);
        }
      };

      window.addEventListener('message', handleShellMessage);
      return () => window.removeEventListener('message', handleShellMessage);
    }

    // ── Standalone mode: direct SSE connection (unchanged behaviour) ──────────
    const token = localStorage.getItem('session_token') || '';

    sseService.connect(token, (event, data) => {
      console.log('🔔 [useSSE] Event received from sseService:', event, data);
      switch (event) {
        case 'account_activated':
          console.log('🔔 [useSSE] Calling onAccountActivated handler');
          handlersRef.current.onAccountActivated?.(data);
          console.log('🔔 [useSSE] onAccountActivated handler completed');
          break;
        case 'account_failed':
          handlersRef.current.onAccountFailed?.(data);
          break;
        case 'workflow_activated':
          handlersRef.current.onWorkflowActivated?.(data);
          break;
        case 'workflow_failed':
          handlersRef.current.onWorkflowFailed?.(data);
          break;
        case 'workflow_completed':
          handlersRef.current.onWorkflowCompleted?.(data);
          break;
        case 'tiktok_qr_ready':
          handlersRef.current.onTikTokQRReady?.(data);
          break;
      }
    });

    return () => {
      sseService.disconnect();
    };
  }, [enabled]);
};
